/**
 * Shared hex ↔ RGB conversion. Lives in `src/lib/color` alongside {@link ./hsl} so the logo
 * extractor, landing-page palette derivation, and color picker all use one implementation
 * instead of each defining their own.
 */

/**
 * Convert a `#rrggbb` (or `rrggbb`) hex color to an 8-bit RGB triple.
 * @param hex - Six-digit hex color, with or without a leading `#`.
 * @returns The `{ r, g, b }` channels (0–255), or null when the string isn't a valid hex color.
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * RGB triple for `#5261EB`, the default enterprise primary color (matches
 * `DEFAULT_THEME.primaryColor` in `EnterpriseProvider.tsx`). Callers fall back to this when
 * {@link hexToRgb} returns `null` for a theme color that should always be valid hex.
 */
export const DEFAULT_PRIMARY_RGB = { r: 82, g: 97, b: 235 };

/**
 * Convert an 8-bit RGB triple to a `#rrggbb` hex string. Channels are rounded and clamped to
 * 0–255 so out-of-range values (e.g. from float HSL math) can't produce malformed hex.
 * @param r - Red channel.
 * @param g - Green channel.
 * @param b - Blue channel.
 * @returns A lowercase `#rrggbb` hex string.
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number): string =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Convert a `#rrggbb` hex color plus an alpha fraction to a CSS `rgba(...)` string, for a
 * semi-transparent layer (e.g. a legibility scrim) whose alpha varies at runtime. The alpha is
 * clamped to 0-1 (and a non-finite alpha becomes 1) so a bad caller value can't emit an invalid
 * declaration the browser silently drops.
 * @param hex - Six-digit hex color, with or without a leading `#`. Falls back to white when invalid.
 * @param alpha - Opacity fraction, 0 (transparent) to 1 (opaque); clamped into that range.
 * @returns A `rgba(r, g, b, alpha)` string.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex) ?? { r: 255, g: 255, b: 255 };
  const safeAlpha = Number.isFinite(alpha) ? Math.max(0, Math.min(1, alpha)) : 1;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${safeAlpha})`;
}

/**
 * Composite `foreground` over `background` at `alpha` and return the flat result, i.e. the color
 * a viewer actually sees when a semi-transparent layer sits over an opaque one. Use for contrast
 * math, where a translucent overlay has to be reduced to the single hex it effectively renders as.
 * @param foregroundHex - The translucent upper layer's color.
 * @param backgroundHex - The opaque lower layer's color.
 * @param alpha - The upper layer's opacity, 0-1; clamped into that range (non-finite becomes 1).
 * @returns The composited `#rrggbb` color, or `backgroundHex` when either input is invalid hex.
 */
export function blendHex(foregroundHex: string, backgroundHex: string, alpha: number): string {
  const fg = hexToRgb(foregroundHex);
  const bg = hexToRgb(backgroundHex);
  if (!fg || !bg) return backgroundHex;
  const a = Number.isFinite(alpha) ? Math.max(0, Math.min(1, alpha)) : 1;
  return rgbToHex(fg.r * a + bg.r * (1 - a), fg.g * a + bg.g * (1 - a), fg.b * a + bg.b * (1 - a));
}
